import * as THREE from 'three';
import { commonGLSL, curvatureGLSL, lightingGLSL } from '../shaders/common';
import { shadowGLSL } from '../render/Shadows';
import { shared } from '../render/SharedUniforms';
import { strataGLSL } from './Strata';

/**
 * Instanced Claron-Formation rock: world-aligned strata (color + ledge displacement + ledge normals),
 * baked AO from Blender vertex colors (R = AO, G = caprock mask, B = normalized height), snow and wetness.
 */
const instancing = /* glsl */ `
in vec4 aInst0; // x y z rotY
in vec4 aInst1; // sx sy sz rand
in vec4 color;
uniform float uLedge;
uniform float uLedgeFade;
mat3 rotY(float a) { float c = cos(a), s = sin(a); return mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c); }
vec3 rockWorld(out vec3 wn, out float fade) {
  mat3 R = rotY(aInst0.w);
  vec3 wp = R * (position * aInst1.xyz) + aInst0.xyz;
  wn = normalize(R * (normal / aInst1.xyz));
  float dist = distance(wp, cameraPosition);
  fade = 1.0 - smoothstep(uLedgeFade * 0.55, uLedgeFade, dist);
  if (fade > 0.0) {
    float hard = strataAtLod(strataY(wp), 0.0).a;
    vec2 hn = wn.xz;
    float hl = length(hn);
    float k = smoothstep(0.25, 0.7, hl) * (1.0 - color.g) * fade;
    if (hl > 0.01) wp.xz += hn / hl * (hard - 0.45) * uLedge * k;
  }
  return wp;
}
`;

const vert = /* glsl */ `
out vec3 vWorld;
out vec3 vNormal;
out vec4 vColor;
out float vFade;
out float vRand;
void main() {
  vec3 wn; float fade;
  vec3 wp = rockWorld(wn, fade);
  vWorld = wp;
  vNormal = wn;
  vColor = color;
  vFade = fade;
  vRand = aInst1.w;
  wp.y -= curvatureDrop(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const depthVert = /* glsl */ `
void main() {
  vec3 wn; float fade;
  vec3 wp = rockWorld(wn, fade);
  wp.y -= curvatureDrop(wp);
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const frag = /* glsl */ `
uniform sampler2D uNoise;
uniform sampler2D uTerrainAO;
uniform vec4 uTerrainAOB; // x0, z0, size
uniform float uLedge;
uniform float uRockSat;
in vec3 vWorld;
in vec3 vNormal;
in vec4 vColor;
in float vFade;
in float vRand;
layout(location = 0) out vec4 fragColor;

vec3 triSample(vec3 p, vec3 w, float s) {
  // returns noise channels (r, g, a) blended triplanar
  vec4 x = texture(uNoise, p.zy * vec2(s, s * 0.35));
  vec4 y = texture(uNoise, p.xz * s);
  vec4 z = texture(uNoise, p.xy * vec2(s, s * 0.35));
  vec4 r = x * w.x + y * w.y + z * w.z;
  return r.rga;
}

void main() {
  vec3 N = normalize(vNormal);
  if (!gl_FrontFacing) N = -N;
  float dist = length(cameraPosition - vWorld);
  float sy = strataY(vWorld);
  vec4 st = strataAt(sy);
  // ledge normals from the hardness profile (crisp at any LOD)
  float e = 0.1;
  float dh = (strataAt(sy + e).a - strataAt(sy - e).a) / (2.0 * e) * uLedge;
  float hl = length(N.xz);
  float ledgeW = smoothstep(0.25, 0.7, hl) * (1.0 - vColor.g) * (1.0 - smoothstep(400.0, 900.0, dist));
  N = normalize(N - vec3(0.0, 1.0, 0.0) * clamp(dh, -3.0, 3.0) * ledgeW * 0.9);

  vec3 w = pow(abs(N), vec3(3.0));
  w /= (w.x + w.y + w.z);
  float detail = 1.0 - smoothstep(60.0, 300.0, dist);
  vec3 n1 = triSample(vWorld, w, 0.33);
  vec3 n2 = triSample(vWorld, w, 1.7);
  vec3 n3 = triSample(vWorld * vec3(1.0, 0.35, 1.0), w, 0.9); // vertically stretched: rain flutes
  // multi-scale bump: weathered blocks, rain flutes, grain
  if (detail > 0.0) {
    vec3 g = (vec3(n2.x, n1.x, n2.y) - 0.5) * 0.3 + (vec3(n3.x, 0.0, n3.z) - 0.5) * 0.45 + (vec3(n2.z, n2.x, n1.y) - 0.5) * 0.18;
    g *= detail;
    N = normalize(N + g - N * dot(N, g));
  }
  // keep the bands subtle: blend toward the locally averaged strata colour
  vec3 alb = mix(st.rgb, strataAtLod(sy, 5.0).rgb, 0.45);
  // caprock: paler, slightly greyer dolomitic limestone
  float cap = vColor.g;
  alb = mix(alb, alb * vec3(1.05, 1.03, 1.0) + vec3(0.03, 0.025, 0.02), cap * 0.6);
  // vertical rain runnels & varnish streaks
  float streak = texture(uNoise, vec2((vWorld.x + vWorld.z) * 0.55 + vRand * 7.0, vWorld.y * 0.03)).r;
  alb *= 0.84 + 0.3 * streak;
  alb *= 0.88 + 0.24 * n1.x;
  // pale mineral bloom on recesses, darker varnish on exposed bulges
  alb *= mix(1.04, 0.94, smoothstep(0.4, 0.7, n3.x));
  // darker, redder recessed soft beds, paler hard ledges
  alb *= mix(0.97, 1.03, st.a);

  // fallen blocks are weathered on all sides: paler, less saturated than in-place beds
  alb = mix(vec3(lum3(alb)), alb, uRockSat) * mix(0.92, 1.0, uRockSat);
  // sky visibility: terrain enclosure near the ground, Blender AO on the formation itself
  vec2 tuv = (vWorld.xz - uTerrainAOB.xy) / uTerrainAOB.z;
  float tvis = texture(uTerrainAO, tuv).b;
  float hN = clamp(vColor.b, 0.0, 1.0);
  float skyVis = mix(tvis, 1.0, smoothstep(0.0, 0.9, hN) * 0.6) * mix(0.55, 1.0, vColor.r);
  float ao = mix(0.6, 1.0, vColor.r);

  // snow on ledges and caps
  float sn = N.y + (n1.y - 0.5) * 0.25;
  float thr = mix(1.05, 0.42, uSnowCover);
  float snow = smoothstep(thr, thr + 0.1, sn) * step(0.001, uSnowCover);
  alb = mix(alb * mix(1.0, 0.62, uWetness), vec3(0.9, 0.91, 0.94), snow);
  float rough = mix(mix(0.88, 0.4, uWetness), 0.55, snow);

  vec3 V = normalize(cameraPosition - vWorld);
  float sh = getShadow(vWorld, normalize(vNormal), gl_FragCoord.xy);
  vec3 bounce = vec3(0.6, 0.33, 0.19);
  vec3 col = shadeSurface(alb, N, V, vWorld, rough, 0.035 + uWetness * 0.02, sh, skyVis, ao, bounce, 0.0);
  fragColor = vec4(col, 1.0);
}
`;

export function createRockMaterials(terrainAO: THREE.Texture, aoBounds: THREE.Vector4, shadowUniforms: Record<string, THREE.IUniform>) {
  const local = {
    uRockSat: { value: 1 },
    uLedge: { value: 0.32 },
    uLedgeFade: { value: 220 },
    uTerrainAO: { value: terrainAO },
    uTerrainAOB: { value: aoBounds },
  };
  const material = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: commonGLSL + curvatureGLSL + strataGLSL + instancing + vert,
    fragmentShader: commonGLSL + lightingGLSL + shadowGLSL + strataGLSL + frag,
    uniforms: { ...shared, ...shadowUniforms, ...local },
  });
  const depthMaterial = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: commonGLSL + curvatureGLSL + strataGLSL + instancing + depthVert,
    fragmentShader: 'layout(location = 0) out vec4 fragColor; void main() { fragColor = vec4(1.0); }',
    uniforms: { ...shared, ...local },
  });
  return { material, depthMaterial, uniforms: local };
}
